import React, { useState, useEffect } from 'react';
import config from '../config';

const SECScheduleForm = ({ schedule, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cik_numbers: [],
    filing_types: ['10-K', '10-Q', '8-K'],
    cadence_value: 1,
    cadence_unit: 'hours',
    is_active: true,
    ms_teams_channel: '',
    prompt_mappings: {},
  });

  const [cikInput, setCikInput] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [teamsChannels, setTeamsChannels] = useState([]);
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(true);

  useEffect(() => {
    if (schedule) {
      setFormData({
        name: schedule.name || '',
        description: schedule.description || '',
        cik_numbers: schedule.cik_numbers || [],
        filing_types: schedule.filing_types || ['10-K', '10-Q', '8-K'],
        cadence_value: schedule.cadence_value || 1,
        cadence_unit: schedule.cadence_unit || 'hours',
        is_active: schedule.is_active !== undefined ? schedule.is_active : true,
        ms_teams_channel: schedule.ms_teams_channel || '',
        prompt_mappings: schedule.prompt_mappings || {},
      });
    }
  }, [schedule]);

  useEffect(() => {
    // Fetch available Teams channels
    const fetchTeamsChannels = async () => {
      try {
        const response = await fetch(`${config.api.secServiceUrl}/sec/teams-channels`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setTeamsChannels(data.channels || []);
        }
      } catch (error) {
        console.error('Error fetching Teams channels:', error);
      }
    };
    fetchTeamsChannels();

    // Fetch available prompts with SEC_ prefix
    const fetchPrompts = async () => {
      try {
        setPromptsLoading(true);
        const response = await fetch(`${config.api.bedrockServiceUrl}/bedrock/prompts?lightweight=true`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Filter prompts that start with SEC_
          const secPrompts = (data.prompts || []).filter(prompt => 
            prompt.name && prompt.name.startsWith('SEC_')
          );
          setAvailablePrompts(secPrompts);
        }
      } catch (error) {
        console.error('Error fetching prompts:', error);
      } finally {
        setPromptsLoading(false);
      }
    };
    fetchPrompts();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleAddCIK = () => {
    const cik = cikInput.trim();
    if (cik && !formData.cik_numbers.includes(cik)) {
      setFormData(prev => ({
        ...prev,
        cik_numbers: [...prev.cik_numbers, cik]
      }));
      setCikInput('');
      setErrors(prev => ({ ...prev, cik_numbers: '' }));
    }
  };

  const handleRemoveCIK = (cik) => {
    setFormData(prev => ({
      ...prev,
      cik_numbers: prev.cik_numbers.filter(c => c !== cik)
    }));
  };

  const handleFilingTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      filing_types: prev.filing_types.includes(type)
        ? prev.filing_types.filter(t => t !== type)
        : [...prev.filing_types, type]
    }));
  };

  const handlePromptMappingChange = (filingType, promptName) => {
    setFormData(prev => ({
      ...prev,
      prompt_mappings: {
        ...prev.prompt_mappings,
        [filingType]: promptName || undefined
      }
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.cik_numbers.length === 0) {
      newErrors.cik_numbers = 'At least one CIK number is required';
    }

    if (formData.filing_types.length === 0) {
      newErrors.filing_types = 'At least one filing type is required';
    }

    if (!formData.cadence_value || formData.cadence_value < 1 || formData.cadence_value > 50) {
      newErrors.cadence_value = 'Cadence must be between 1 and 50';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSave(formData);
    } catch (err) {
      console.error('Error saving schedule:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save schedule';
      setErrors({ submit: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const commonFilingTypes = [
    { value: '10-K', label: '10-K (Annual Report)' },
    { value: '10-Q', label: '10-Q (Quarterly Report)' },
    { value: '8-K', label: '8-K (Current Report)' },
    { value: '4', label: 'Form 4 (Insider Trading)' },
    { value: 'S-1', label: 'S-1 (IPO Registration)' },
    { value: 'DEF 14A', label: 'DEF 14A (Proxy Statement)' },
  ];

  return (
    <form onSubmit={handleSubmit}>
      {errors.submit && (
        <div className="alert alert-danger" role="alert">
          {errors.submit}
        </div>
      )}

      {/* Name */}
      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          Schedule Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="e.g., Daily Tech Company Filings"
        />
        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
      </div>

      {/* Description */}
      <div className="mb-3">
        <label htmlFor="description" className="form-label">Description</label>
        <textarea
          className="form-control"
          id="description"
          name="description"
          rows="2"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Optional description of this schedule"
        />
      </div>

      {/* CIK Numbers */}
      <div className="mb-3">
        <label className="form-label">
          CIK Numbers <span className="text-danger">*</span>
        </label>
        <div className="input-group mb-2">
          <input
            type="text"
            className={`form-control ${errors.cik_numbers ? 'is-invalid' : ''}`}
            value={cikInput}
            onChange={(e) => setCikInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCIK())}
            placeholder="Enter CIK number (e.g., 0000789019 for Microsoft)"
          />
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleAddCIK}
          >
            Add
          </button>
          {errors.cik_numbers && <div className="invalid-feedback d-block">{errors.cik_numbers}</div>}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {formData.cik_numbers.map(cik => (
            <span key={cik} className="badge bg-primary">
              {cik}
              <button
                type="button"
                className="btn-close btn-close-white ms-2"
                style={{ fontSize: '0.6rem' }}
                onClick={() => handleRemoveCIK(cik)}
                aria-label="Remove"
              ></button>
            </span>
          ))}
        </div>
        <small className="form-text text-muted">
          Find CIK numbers at <a href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany" target="_blank" rel="noopener noreferrer">SEC EDGAR</a>
        </small>
      </div>

      {/* Filing Types & Analysis Prompts */}
      <div className="mb-3">
        <label className="form-label">
          Filing Types & Analysis Prompts <span className="text-danger">*</span>
          <i className="bi bi-info-circle ms-2" title="Select filing types to monitor and optionally assign analysis prompts. Filings will be automatically analyzed when found."></i>
        </label>
        {errors.filing_types && <div className="text-danger small mb-2">{errors.filing_types}</div>}
        
        {promptsLoading ? (
          <div className="text-muted">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Loading prompts...
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              {commonFilingTypes.map(type => (
                <div key={type.value} className="row mb-3 align-items-center">
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`filing-type-${type.value}`}
                        checked={formData.filing_types.includes(type.value)}
                        onChange={() => handleFilingTypeToggle(type.value)}
                      />
                      <label className="form-check-label" htmlFor={`filing-type-${type.value}`}>
                        {type.label}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-8">
                    {formData.filing_types.includes(type.value) && (
                      <select
                        className="form-select form-select-sm"
                        value={formData.prompt_mappings[type.value] || ''}
                        onChange={(e) => handlePromptMappingChange(type.value, e.target.value)}
                      >
                        <option value="">Default</option>
                        {availablePrompts.map(prompt => (
                          <option key={prompt.id} value={prompt.name}>
                            {prompt.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <small className="form-text text-muted d-block mt-2">
          When new filings are found, they will be automatically analyzed using the selected prompts and results included in notifications
        </small>
      </div>

      {/* Polling Cadence */}
      <div className="mb-3">
        <label className="form-label">
          Polling Frequency <span className="text-danger">*</span>
        </label>
        <div className="row">
          <div className="col-md-6">
            <input
              type="number"
              className={`form-control ${errors.cadence_value ? 'is-invalid' : ''}`}
              name="cadence_value"
              value={formData.cadence_value}
              onChange={handleInputChange}
              min="1"
              max="50"
              placeholder="1-50"
            />
            {errors.cadence_value && <div className="invalid-feedback">{errors.cadence_value}</div>}
          </div>
          <div className="col-md-6">
            <select
              className="form-select"
              name="cadence_unit"
              value={formData.cadence_unit}
              onChange={handleInputChange}
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
        </div>
        <small className="form-text text-muted">
          How often to check for new filings
        </small>
      </div>

      {/* MS Teams Channel */}
      <div className="mb-3">
        <label htmlFor="ms_teams_channel" className="form-label">
          MS Teams Notification Channel
        </label>
        <select
          className="form-select"
          id="ms_teams_channel"
          name="ms_teams_channel"
          value={formData.ms_teams_channel}
          onChange={handleInputChange}
        >
          <option value="">None (no notifications)</option>
          {teamsChannels.map(channel => (
            <option key={channel} value={channel}>
              {channel}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">
          Send notifications to MS Teams when new filings are found
        </small>
      </div>

      {/* Active Status */}
      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="is_active"
          name="is_active"
          checked={formData.is_active}
          onChange={handleInputChange}
        />
        <label className="form-check-label" htmlFor="is_active">
          Active (schedule will run automatically)
        </label>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            'Save Schedule'
          )}
        </button>
      </div>
    </form>
  );
};

export default SECScheduleForm;
